import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BopBackLink — Pulsante "torna alla lista" standard per pagine di dettaglio
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Standard UX BOP v3 (vedi .claude/BOP-FRONTEND-SKILL.md §3.10):
 *
 * Ogni pagina di dettaglio/scheda (`/:id`) torna alla propria lista tramite
 * QUESTO componente. Garantisce uniformità di:
 *   - Icona (freccia sinistra 16×16, stroke 2 — coerente con RowIcon)
 *   - Stile e posizione (sopra il titolo pagina, allineato a sinistra)
 *   - Comportamento (rotta esplicita alla lista, non history-back)
 *   - Dicitura ("← Torna a[i/lle/ll'] <entità>")
 *
 * Regole:
 *   - `to` deve essere la ROTTA ESPLICITA della lista (es. '/commerciale/preventivi'),
 *     mai navigate(-1): la lista è sempre prevedibile anche arrivando da link diretto.
 *   - `label` segue la convenzione "Torna a..." con l'articolo corretto:
 *     "Torna ai preventivi", "Torna alle polizze", "Torna all'elenco movimenti".
 *
 * Usage:
 * ```tsx
 * import BopBackLink from '../../components/BopBackLink';
 *
 * <BopBackLink to="/commerciale/preventivi" label="Torna ai preventivi" />
 * ```
 *
 * Per casi in cui la destinazione non è una rotta (es. wizard a step interni
 * o chiusura di una sotto-vista in pagina), usare `onClick` invece di `to`.
 */

const ArrowLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

interface Props {
  /** Rotta esplicita della lista a cui tornare (es. '/commerciale/preventivi'). */
  to?: string;
  /** Handler alternativo a `to` per destinazioni non-rotta (step wizard, sotto-viste). */
  onClick?: () => void;
  /** Dicitura completa, convenzione "Torna a..." (es. "Torna ai preventivi"). */
  label: string;
}

export default function BopBackLink({ to, onClick, label }: Props) {
  const content = (
    <>
      <ArrowLeftIcon />
      <span>{label}</span>
    </>
  );

  if (to) {
    return (
      <Link to={to} className="bop-back-link">
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className="bop-back-link" onClick={onClick}>
      {content}
    </button>
  );
}
